import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NodeRegistry, NodeRegistryEntry } from '../../types';
import classes from './NodePalette.module.css';

export type NodePaletteProps = {
  open: boolean;
  registry: NodeRegistry;
  position?: { x: number; y: number };
  onSelectNode: ( nodeType: string ) => void;
  onClose: () => void;
};

/** Category color dots — matches Blender-style category palette */
const CATEGORY_COLORS: Record<string, string> = {
  aggregation: '#f59e0b',
  commands: '#ef4444',
  input: '#10b981',
  lists: '#8b5cf6',
  logic: '#60a5fa',
  objects: '#a78bfa',
  operations: '#f97316',
  queries: '#06b6d4',
  strings: '#ec4899',
  utility: '#6b7280'
};

type CategoryTree = {
  name: string;
  nodes: NodeRegistryEntry[];
  subcategories: Map<string, NodeRegistryEntry[]>;
};

/**
 * Build a hierarchical tree from the flat registry.
 * Top-level = category; commands and queries get nested subcategories.
 */
function buildCategoryTree( registry: NodeRegistry ): CategoryTree[] {
  const map = new Map<string, CategoryTree>();

  for ( const entry of Object.values( registry )) {
    const cat = entry.category;
    if ( !map.has( cat )) {
      map.set( cat, { name: cat, nodes: [], subcategories: new Map() });
    }
    const tree = map.get( cat )!;

    if ( entry.subcategory ) {
      const sub = entry.subcategory;
      if ( !tree.subcategories.has( sub )) {
        tree.subcategories.set( sub, []);
      }
      tree.subcategories.get( sub )!.push( entry );
    } else {
      tree.nodes.push( entry );
    }
  }

  // Sort categories alphabetically, but put commands and queries last
  const order = [ 'input', 'aggregation', 'lists', 'logic', 'objects', 'operations', 'strings', 'utility', 'commands', 'queries' ];
  return [...map.values()].sort(( a, b ) => {
    const ai = order.indexOf( a.name );
    const bi = order.indexOf( b.name );
    return ( ai === -1 ? 999 : ai ) - ( bi === -1 ? 999 : bi );
  });
}

/**
 * Count total nodes in a category tree (including subcategories).
 */
function countNodes( tree: CategoryTree ): number {
  let count = tree.nodes.length;
  for ( const nodes of tree.subcategories.values()) {
    count += nodes.length;
  }
  return count;
}

/**
 * Simple fuzzy match: every character of the query must appear
 * in order within the target string.
 */
function fuzzyMatch( query: string, target: string ): boolean {
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  let qi = 0;
  for ( let ti = 0; ti < t.length && qi < q.length; ti++ ) {
    if ( t[ ti ] === q[ qi ]) {
      qi++;
    }
  }
  return qi === q.length;
}

/**
 * Filter registry entries by search query (fuzzy match on name + description).
 */
function filterEntries( registry: NodeRegistry, query: string ): NodeRegistryEntry[] {
  if ( !query.trim()) return [];
  return Object.values( registry ).filter(( entry ) =>
    fuzzyMatch( query, entry.name ) ||
    ( entry.description && fuzzyMatch( query, entry.description )));
}

/**
 * Format a category/subcategory name for display.
 * Converts kebab-case to Title Case.
 */
function formatLabel( name: string ): string {
  return name
    .split( '-' )
    .map(( w ) => w.charAt( 0 ).toUpperCase() + w.slice( 1 ))
    .join( ' ' );
}

/* ----- Chevron SVG ----- */
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`${classes.chevron} ${open ? classes.chevronOpen : ''}`}
      viewBox="0 0 16 16"
      fill="currentColor"
    >
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

/* ======================================================================== */

export function NodePalette({ open, registry, position, onSelectNode, onClose }: NodePaletteProps ) {
  const [ search, setSearch ] = useState( '' );
  const [ expandedCategories, setExpandedCategories ] = useState<Set<string>>(() => new Set());
  const [ expandedSubcategories, setExpandedSubcategories ] = useState<Set<string>>(() => new Set());
  const searchRef = useRef<HTMLInputElement>( null );
  const panelRef = useRef<HTMLDivElement>( null );

  // Reset state when panel opens
  useEffect(() => {
    if ( open ) {
      setSearch( '' );
      setExpandedCategories( new Set());
      setExpandedSubcategories( new Set());
      // Focus search input after render
      requestAnimationFrame(() => {
        searchRef.current?.focus();
      });
    }
  }, [open]);

  // Handle Escape key
  useEffect(() => {
    if ( !open ) return;
    const handleKey = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener( 'keydown', handleKey, true );
    return () => window.removeEventListener( 'keydown', handleKey, true );
  }, [ open, onClose ]);

  const categoryTree = useMemo(() => buildCategoryTree( registry ), [registry]);
  const searchResults = useMemo(() => filterEntries( registry, search ), [ registry, search ]);
  const isSearching = search.trim().length > 0;

  const toggleCategory = useCallback(( name: string ) => {
    setExpandedCategories(( prev ) => {
      const next = new Set( prev );
      if ( next.has( name )) {
        next.delete( name );
      } else {
        next.add( name );
      }
      return next;
    });
  }, []);

  const toggleSubcategory = useCallback(( key: string ) => {
    setExpandedSubcategories(( prev ) => {
      const next = new Set( prev );
      if ( next.has( key )) {
        next.delete( key );
      } else {
        next.add( key );
      }
      return next;
    });
  }, []);

  const handleSelect = useCallback(( nodeType: string ) => {
    onSelectNode( nodeType );
    onClose();
  }, [ onSelectNode, onClose ]);

  const handleOverlayClick = useCallback(( e: React.MouseEvent ) => {
    if ( e.target === e.currentTarget ) {
      onClose();
    }
  }, [onClose]);

  if ( !open ) return null;

  const panelStyle: React.CSSProperties = position
    ? { left: position.x, top: position.y }
    : { left: '50%', top: '20%', transform: 'translateX(-50%)' };

  return (
    <div className={classes.overlay} onClick={handleOverlayClick}>
      <div ref={panelRef} className={classes.panel} style={panelStyle}>
        {/* Search */}
        <div className={classes.searchWrapper}>
          <input
            ref={searchRef}
            className={classes.searchInput}
            type="text"
            placeholder="Search nodes..."
            value={search}
            onChange={( e ) => setSearch( e.target.value )}
          />
        </div>

        {/* Node list */}
        <div className={classes.list}>
          {isSearching ? (
            /* Search results — flat list */
            searchResults.length > 0 ? (
              searchResults.map(( entry ) => (
                <div
                  key={entry.name}
                  className={classes.nodeItem}
                  onClick={() => handleSelect( entry.name )}
                >
                  <span
                    className={classes.nodeIcon}
                    style={{ background: CATEGORY_COLORS[ entry.category ] || '#6b7280' }}
                  />
                  <span className={classes.nodeName}>{entry.name}</span>
                  {entry.subcategory && (
                    <span className={classes.nodeDescription}>
                      {formatLabel( entry.subcategory )}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className={classes.emptyState}>
                <div className={classes.emptyStateTitle}>No matching nodes</div>
                <div>Try a different search term</div>
              </div>
            )
          ) : (
            /* Category tree */
            categoryTree.map(( cat ) => {
              const isExpanded = expandedCategories.has( cat.name );
              const total = countNodes( cat );
              const hasSubcategories = cat.subcategories.size > 0;

              return (
                <div key={cat.name} className={classes.category}>
                  <div
                    className={classes.categoryHeader}
                    onClick={() => toggleCategory( cat.name )}
                  >
                    <ChevronIcon open={isExpanded} />
                    <span className={classes.categoryLabel}>{formatLabel( cat.name )}</span>
                    <span className={classes.badge}>{total}</span>
                  </div>

                  {isExpanded && (
                    <div className={classes.categoryItems}>
                      {/* Direct nodes (no subcategory) */}
                      {cat.nodes.map(( entry ) => (
                        <div
                          key={entry.name}
                          className={classes.nodeItem}
                          onClick={() => handleSelect( entry.name )}
                        >
                          <span
                            className={classes.nodeIcon}
                            style={{ background: CATEGORY_COLORS[ cat.name ] || '#6b7280' }}
                          />
                          <span className={classes.nodeName}>{entry.name}</span>
                          {entry.description && (
                            <span className={classes.nodeDescription}>{entry.description}</span>
                          )}
                        </div>
                      ))}

                      {/* Subcategories */}
                      {hasSubcategories && (
                        [...cat.subcategories.entries()]
                          .sort(([a], [b]) => a.localeCompare( b ))
                          .map(([ subName, nodes ]) => {
                            const subKey = `${cat.name}/${subName}`;
                            const subExpanded = expandedSubcategories.has( subKey );

                            return (
                              <div key={subKey}>
                                <div
                                  className={classes.subcategoryHeader}
                                  onClick={() => toggleSubcategory( subKey )}
                                >
                                  <ChevronIcon open={subExpanded} />
                                  <span className={classes.categoryLabel}>
                                    {formatLabel( subName )}
                                  </span>
                                  <span className={classes.badge}>{nodes.length}</span>
                                </div>

                                {subExpanded && (
                                  <div className={classes.subcategoryItems}>
                                    {nodes
                                      .sort(( a, b ) => a.name.localeCompare( b.name ))
                                      .map(( entry ) => (
                                        <div
                                          key={entry.name}
                                          className={`${classes.nodeItem} ${classes.nodeItemNested}`}
                                          onClick={() => handleSelect( entry.name )}
                                        >
                                          <span
                                            className={classes.nodeIcon}
                                            style={{ background: CATEGORY_COLORS[ cat.name ] || '#6b7280' }}
                                          />
                                          <span className={classes.nodeName}>{entry.name}</span>
                                          {entry.description && (
                                            <span className={classes.nodeDescription}>
                                              {entry.description}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            );
                          })
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div className={classes.footer}>
          <span><kbd className={classes.kbd}>Esc</kbd> close</span>
          <span><kbd className={classes.kbd}>Shift+A</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
}
